import {
    useEffect, useState, useContext
} from 'react';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Button
} from 'shared/components/ui/button';
import {
    useParams
} from 'react-router-dom';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    outlookConfigService
} from 'shared/api';

export default function ConfigMailSecretsInline({
    onDone
}: {
    onDone?: () => void;
}) {
    const {
        oragID
    } = useParams();
    const [clientId, setClientId] = useState('');
    const [clientSecret, setClientSecret] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [outlookConfigured, setOutlookConfigured] = useState(false);
    const [message, setMessage] = useState('');

    const {
        getUserRole
    } = useContext(OrganizationContext);
    const roleInstance = getUserRole();
    const isSuperAdmin = roleInstance.isSuperAdmin;

    useEffect(() => {
        if (!isSuperAdmin) {
            setLoading(false);
            return;
        }

        setLoading(true);
        outlookConfigService.getOutlookConfig(oragID)
            .then((response) => {
                const outlook = response.data?.outlook;
                if (outlook?.clientId && outlook?.clientSecret) {
                    setClientId(outlook.clientId);
                    setClientSecret(outlook.clientSecret);
                    setOutlookConfigured(true);
                }
            })
            .catch(() => {

                setMessage('Failed to load Outlook credentials.');
            })
            .finally(() => setLoading(false));
    }, [oragID, isSuperAdmin]);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!isSuperAdmin) return;

        setSaving(true);
        setMessage('');
        try {
            await outlookConfigService.createOutlookConfig({
                organizationId: oragID,
                clientId,
                clientSecret,
            });
            setMessage('Outlook credentials saved successfully.');
            setOutlookConfigured(true);
            if (onDone) onDone();
        } catch {
            setMessage('Failed to save credentials.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!isSuperAdmin) return;
        if (!window.confirm('Are you sure you want to remove Outlook credentials?')) return;

        setDeleting(true);
        setMessage('');
        try {
            await outlookConfigService.deleteOutlookConfig(oragID);
            setClientId('');
            setClientSecret('');
            setOutlookConfigured(false);
            setMessage('Outlook credentials deleted.');
            if (onDone) onDone();
        } catch {
            setMessage('Failed to delete credentials.');
        } finally {
            setDeleting(false);
        }
    };

    if (!isSuperAdmin) {
        return (
            <div className="p-4 border rounded shadow bg-white">
                <h3 className="text-lg font-semibold mb-4">Outlook Secrets</h3>
                <p className="text-muted-foreground">Only super admins can view and manage Outlook credentials</p>
            </div>
        );
    }

    return (
        <div className="p-4 border rounded shadow bg-white">
            <h3 className="text-lg font-semibold mb-4">Outlook Secrets</h3>
            {message && (
                <div className={`mb-4 text-sm ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                    {message}
                </div>
            )}
            {loading ? (
                <p>Loading...</p>
            ) : (
                <form onSubmit={handleSave} className="space-y-6">
                    <div>
                        <Label>Client ID</Label>
                        <Input
                            value={clientId}
                            onChange={e => setClientId(e.target.value)}
                            placeholder="Enter Outlook Client ID"
                            disabled={saving}
                            required
                            name='outlook-client-id'
                            autoComplete='new-password'
                        />
                    </div>
                    <div>
                        <Label>Client Secret</Label>
                        <Input
                            value={clientSecret}
                            onChange={e => setClientSecret(e.target.value)}
                            placeholder="Enter Outlook Client Secret"
                            disabled={saving}
                            required
                            name='outlook-password'
                            type="password"
                            autoComplete='new-password'
                        />
                    </div>
                    <div className="flex gap-4 justify-end">
                        <Button type="submit" disabled={saving}>
                            {outlookConfigured ? 'Update' : 'Save'}
                        </Button>
                        {outlookConfigured && (
                            <Button type="button" variant="outline" onClick={handleDelete} disabled={deleting}>
                                Delete
                            </Button>
                        )}
                    </div>
                </form>
            )}
        </div>
    );
}